import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useChangeSolarPlantStatusMutation,
  useLazyLocalViewCustomerPlantDetailsQuery,
  useLazyViewCustomerPlantDetailsQuery,
} from "../../../redux/api/generation/generationAPI";
import { SiteAccessRequiredState } from "../../../shared/components/access-state-card/access-states";
import { ESolarServiceProvider } from "../../../shared/oversight-core/enums/solar-service-provider";
import { IGoodWeSolarPlantView } from "../../../shared/oversight-core/interfaces/good-we-solar-plant-view";
import { IGroWattSolarPlantView } from "../../../shared/oversight-core/interfaces/gro-watt-solar-plant-view";
import { IHttpError } from "../../../shared/oversight-core/interfaces/http-errror";
import { IPowerHubSolarPlantView } from "../../../shared/oversight-core/interfaces/powerhub-solar-plant-view";
import { IScoutSolarPlantView } from "../../../shared/oversight-core/interfaces/scout-solar-plant-view";
import { IVrmSolarPlantView } from "../../../shared/oversight-core/interfaces/vrm-solar-plant-view";
import SpinnerModal from "../../../shared/oversight-core/ui-elements/spinner/spinner";
import { isScopeRefusal } from "../../../shared/oversight-core/utils/is-scope-refusal";
import ViewSiteContent from "./view-site-content";
import {
  applySolarPlantView,
  clearSolarPlantView,
  confirmSiteStatusChange,
  getSiteViewModel,
  loadPlantDetails,
  loadRemotePlantDetails,
  SitePlants,
  SolarPlantView,
  startRemotePlantPolling,
  syncActiveTab,
} from "./view-site-helpers";
import ViewSiteModals from "./view-site-modals";

const useViewSitePlant = (
  plantId: string,
  serviceProvider: ESolarServiceProvider,
  activeTabState: number,
) => {
  const [activeTab, setActiveTab] = useState<number>(1);
  const [vrmSolarPlant, setVrmSolarPlant] = useState<IVrmSolarPlantView>();
  const [goodWeSolarPlant, setGoodWeSolarPlant] =
    useState<IGoodWeSolarPlantView>();
  const [groWattSolarPlantView, setGroWattSolarPlantView] =
    useState<IGroWattSolarPlantView>();
  const [powerHubSolarPlant, setPowerHubSolarPlant] =
    useState<IPowerHubSolarPlantView>();
  const [scoutSolarPlant, setScoutSolarPlant] =
    useState<IScoutSolarPlantView>();
  const [isViewingSolarPlant, setIsViewingSolarPlant] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [updateCurrentState, setUpdateCurrentState] = useState(false);
  // The plant a 401 scope refusal was for: it exists but isn't in the caller's access.
  // Keyed by plant, so opening another site on this route never inherits the denial.
  const [deniedPlantKey, setDeniedPlantKey] = useState<string>();
  const plantKey = `${serviceProvider}:${plantId}`;
  const isAccessDenied = deniedPlantKey === plantKey;

  const [triggerCustomerPlantDetails] = useLazyViewCustomerPlantDetailsQuery();
  const [triggerLocalViewCustomerPlantDetails] =
    useLazyLocalViewCustomerPlantDetailsQuery();

  const plantViewSetters = useMemo(
    () => ({
      setVrmSolarPlant,
      setGoodWeSolarPlant,
      setGroWattSolarPlantView,
      setPowerHubSolarPlant,
      setScoutSolarPlant,
      setIsViewingSolarPlant,
    }),
    [
      setVrmSolarPlant,
      setGoodWeSolarPlant,
      setGroWattSolarPlantView,
      setPowerHubSolarPlant,
      setScoutSolarPlant,
      setIsViewingSolarPlant,
    ],
  );

  useEffect(() => {
    syncActiveTab(activeTabState, setActiveTab);
  }, [activeTabState]);

  // Callbacks for one run of plant requests. Once the run is superseded (another plant,
  // a refresh, unmount) its late responses are ignored, so they can't set the denial for,
  // or overwrite the view of, the plant now on screen. A success clears the plant's denial.
  const bindPlantCallbacks = useCallback(
    (requestKey: string) => {
      let isCurrent = true;
      return {
        onApply: (solarPlantView: SolarPlantView) => {
          if (!isCurrent) return;
          setDeniedPlantKey((key) => (key === requestKey ? undefined : key));
          applySolarPlantView(solarPlantView, plantViewSetters);
        },
        onError: (error?: IHttpError) => {
          if (!isCurrent) return;
          if (isScopeRefusal(error)) setDeniedPlantKey(requestKey);
          clearSolarPlantView(plantViewSetters);
        },
        cancel: () => {
          isCurrent = false;
        },
      };
    },
    [plantViewSetters],
  );

  useEffect(() => {
    const callbacks = bindPlantCallbacks(plantKey);
    loadPlantDetails(
      plantId,
      triggerLocalViewCustomerPlantDetails,
      callbacks.onApply,
      callbacks.onError,
      () => setIsLoading(false),
    );
    return callbacks.cancel;
  }, [
    triggerLocalViewCustomerPlantDetails,
    plantId,
    plantKey,
    updateCurrentState,
    bindPlantCallbacks,
  ]);

  useEffect(() => {
    const callbacks = bindPlantCallbacks(plantKey);
    loadRemotePlantDetails(
      serviceProvider,
      plantId,
      triggerCustomerPlantDetails,
      callbacks.onApply,
      callbacks.onError,
      () => setIsLoading(false),
    );
    return callbacks.cancel;
  }, [
    triggerCustomerPlantDetails,
    plantId,
    plantKey,
    serviceProvider,
    bindPlantCallbacks,
  ]);

  useEffect(() => {
    // Polling a site the caller can't access would only repeat the refusal; a refresh or
    // another plant brings it back.
    if (isAccessDenied) return undefined;

    const callbacks = bindPlantCallbacks(plantKey);
    const stopPolling = startRemotePlantPolling(
      serviceProvider,
      plantId,
      triggerCustomerPlantDetails,
      callbacks.onApply,
      callbacks.onError,
    );
    return () => {
      callbacks.cancel();
      stopPolling?.();
    };
  }, [
    triggerCustomerPlantDetails,
    plantId,
    plantKey,
    serviceProvider,
    bindPlantCallbacks,
    isAccessDenied,
  ]);

  const plants: SitePlants = {
    vrm: vrmSolarPlant,
    goodWe: goodWeSolarPlant,
    growatt: groWattSolarPlantView,
    powerHub: powerHubSolarPlant,
    scout: scoutSolarPlant,
  };

  return {
    isLoading,
    isAccessDenied,
    plants,
    isViewingSolarPlant,
    activeTab,
    setActiveTab,
    refreshPlant: () => setUpdateCurrentState((previous) => !previous),
  };
};

const ViewSite = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const plantId = location.state.id as string;
  const serviceProvider = location.state
    .serviceProvider as ESolarServiceProvider;
  const activeTabState = location.state.activeTab as number;

  const {
    isLoading,
    isAccessDenied,
    plants,
    isViewingSolarPlant,
    activeTab,
    setActiveTab,
    refreshPlant,
  } = useViewSitePlant(plantId, serviceProvider, activeTabState);

  const [showDisableSiteModal, setShowDisableSiteModal] = useState(false);
  const [showEditSiteModal, setShowEditSiteModal] = useState(false);
  const [showChangeLogModal, setShowChangeLogModal] = useState(false);
  const [showExportSiteReportModal, setShowExportSiteReportModal] =
    useState(false);

  const [
    triggerChangeSolarPlantStatus,
    { isLoading: isLoadingChangeSolarPlantStatus },
  ] = useChangeSolarPlantStatusMutation();

  const site = getSiteViewModel(serviceProvider, plants);

  if (isAccessDenied) {
    return <SiteAccessRequiredState siteName={location.state.name} />;
  }

  return (
    <>
      {isLoading ? (
        <SpinnerModal show={isLoading} />
      ) : (
        <ViewSiteContent
          plantId={plantId}
          serviceProvider={serviceProvider}
          site={site}
          plants={plants}
          isViewingSolarPlant={isViewingSolarPlant}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onBack={() => navigate(-1)}
          onDisable={() => setShowDisableSiteModal(true)}
          onEdit={() => setShowEditSiteModal(true)}
          onChangeLog={() => setShowChangeLogModal(true)}
          onExport={() => setShowExportSiteReportModal(true)}
        />
      )}
      <ViewSiteModals
        plantId={plantId}
        site={site}
        showDisableSiteModal={showDisableSiteModal}
        showEditSiteModal={showEditSiteModal}
        showChangeLogModal={showChangeLogModal}
        showExportSiteReportModal={showExportSiteReportModal}
        isLoadingChangeSolarPlantStatus={isLoadingChangeSolarPlantStatus}
        onCloseDisable={() => setShowDisableSiteModal(false)}
        onCloseEdit={() => setShowEditSiteModal(false)}
        onCloseChangeLog={() => setShowChangeLogModal(false)}
        onCloseExport={() => setShowExportSiteReportModal(false)}
        onDisableConfirm={() =>
          confirmSiteStatusChange(
            site.identity,
            site.isActive,
            triggerChangeSolarPlantStatus,
            navigate,
          )
        }
        onEditSuccess={refreshPlant}
        onChangeLogSuccess={refreshPlant}
      />
    </>
  );
};

export default ViewSite;
